import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Building2, Wallet, Handshake, ShieldCheck, Loader2,
  AlertTriangle, Phone, Mail, FileDown, ChevronDown, Info, GitCompare,
} from "lucide-react";
import api from "@/lib/api";

/**
 * R110.58 — Phase 3 · Dual-Path Recommendations.
 * R110.130 — Removed the inline "Compare with Do-It-Yourself" toggle
 * per user request; the DIY blueprint lives on the venture page and
 * franchise cards focus purely on the connect + unlock flow.
 *
 * Props:
 *   businessIdeaId, businessIdeaName, targetCity, userProfile, limit
 *   quotaLeft    — remaining consents in the user's 24h window (>=0)
 *   onLeadCreated() — parent callback so it can refresh the shared quota
 */
const inrCompact = (v) => {
  const n = Number(v || 0);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return "₹" + n.toLocaleString("en-IN");
};

export function FranchiseMatchSection({
  businessIdeaId,
  businessIdeaName = "this business",
  targetCity = "",
  diyBaseline = null,
  limit = 3,
  selectedIds = new Set(),
  onToggleSelect = null,
  unlocked = {},          // {franchise_id: {brand_name, brochure_url, owner_email, owner_phone}}
  quotaLeft = 5,
  atCap = false,
  onCapAttempt = null,
  spotlightBrandId = "",  // R110.126 — Deep-link target brand.
  spotlightScroll = true,  // L2 fix: the brand's own page keeps its header in view
  hideCompare = false,     // Hide the "Compare this business" CTA (brand public page).
  directory = false,       // Directory mode: no tick-to-connect; each card links to the
                           // brand's public page where the login/unlock gate lives.
}) {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const GROUP_ORDER = [
    "Franchise Opportunities", "Products & Equipment Suppliers",
    "Raw Material & Inventory Suppliers", "Professional Services",
    "Business Setup & Support",
  ];
  // Group matched brands by their Business-Partner parent category so each
  // category renders as its own labelled block on the idea page.
  const groupedRows = useMemo(() => {
    const m = {};
    for (const f of rows) {
      const g = f.category_group || "Other";
      (m[g] = m[g] || []).push(f);
    }
    const keys = [...GROUP_ORDER.filter((g) => m[g]), ...Object.keys(m).filter((g) => !GROUP_ORDER.includes(g))];
    return keys.map((g) => [g, m[g]]);
    // eslint-disable-next-line
  }, [rows]);
  const [autoSelected, setAutoSelected] = useState(false);
  // R110.150 — Click-to-read brand details (public, no PII).
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  useEffect(() => {
    if (!businessIdeaId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        // R110.140 — Always fetch up to 6 so we can (a) surface the
        // spotlight brand even if it's normally ranked last, and
        // (b) let the SecondBrandNudge pick from the same pool without
        // a second network call. The `limit` prop then just controls
        // how many render initially — the rest surface via the nudge.
        const params = new URLSearchParams({ limit: "6" });
        if (targetCity) params.set("target_city", targetCity);
        const { data } = await api.get(
          `/franchises/for-idea/${encodeURIComponent(businessIdeaId)}?${params}`
        );
        if (!cancelled) {
          let list = Array.isArray(data?.franchises) ? data.franchises : [];
          // Promote the spotlight brand to the front so it's always
          // visible within the first `limit` rows.
          if (spotlightBrandId) {
            const idx = list.findIndex((f) => f.id === spotlightBrandId);
            if (idx > 0) list = [list[idx], ...list.filter((_, i) => i !== idx)];
          }
          setRows(list.slice(0, Math.max(1, limit)));
        }
      } catch (_e) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [businessIdeaId, targetCity, limit, spotlightBrandId]);

  // R110.126 — When the section arrives with a `spotlightBrandId` deep-link,
  // auto-tick the brand card (once), scroll to it, and let the sticky
  // "Connect & unlock" bar surface the CPL-debit CTA immediately.
  useEffect(() => {
    if (!spotlightBrandId || autoSelected || loading) return;
    const match = rows.find((r) => r.id === spotlightBrandId);
    if (!match) return;
    setAutoSelected(true);
    if (!selectedIds.has(match.id) && !atCap && typeof onToggleSelect === "function") {
      onToggleSelect(match);
    }
    // Wait one paint so the ring/pulse renders before scrolling.
    if (spotlightScroll) setTimeout(() => {
      const el = document.querySelector(`[data-testid="franchise-card-${match.id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, loading, spotlightBrandId, autoSelected]);

  const handleToggle = (franchise) => {
    const isSelected = selectedIds.has(franchise.id);
    if (!isSelected && atCap) {
      // Trying to select a 6th — trigger the parent's cap modal.
      if (typeof onCapAttempt === "function") onCapAttempt();
      return;
    }
    if (typeof onToggleSelect === "function") onToggleSelect(franchise);
  };

  if (!businessIdeaId) return null;
  // R110.98 — Hide the entire Path B block for ideas with zero active
  // brands. Empty-state "No ready-made brands are currently active…"
  // card removed per user request; DIY blueprint above stands on its own.
  if (!loading && rows.length === 0) return null;

  return (
    <div className="mt-8" data-testid={`franchise-match-section-${businessIdeaId}`}>
      {/* R110.178 — Platform-wide franchise-brand theme: navy panel
          (same as the blog sidebar promo) with white cards. */}
      <div className="rounded-2xl border-2 border-[#0a1f44] bg-gradient-to-b from-[#0a1f44] via-[#0d2653] to-[#0a1f44] p-5 sm:p-6 shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-300 font-bold">
              Ready-made brands
            </div>
            <h2 className="font-heading mt-2 text-xl sm:text-2xl font-bold text-white">
              Verified brands for {businessIdeaName}
            </h2>
            <div className="mt-1 text-xs text-slate-300">
              {directory
                ? "Verified brands · view full details & contact"
                : "Up to 3 brands per business type · Tick to connect"}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 self-start">
            <Badge className="rounded-full bg-white/10 text-white border border-white/25 hover:bg-white/10">
              <Handshake className="h-3 w-3 mr-1" /> Verified brands · Safe to share
            </Badge>
            {/* R118 — Featured brands had no compare path; add a direct
                "Compare this business" option on the brand panel. */}
            {!hideCompare && !directory && (
            <Button
              variant="outline"
              size="sm"
              data-testid={`franchise-compare-${businessIdeaId}`}
              onClick={(e) => { e.stopPropagation(); nav(`/compare?mode=my-vs-rec&slugs=${encodeURIComponent(businessIdeaId)}`); }}
              className="rounded-full border-white/40 bg-white/10 text-white hover:bg-white/20 h-8 text-xs"
            >
              <GitCompare className="h-3.5 w-3.5 mr-1" /> Compare this business
            </Button>
            )}
          </div>
        </div>

        {loading && (
          <div className="mt-5 flex items-center gap-2 text-sm text-slate-500"
               data-testid={`franchise-match-loading-${businessIdeaId}`}>
            <Loader2 className="h-4 w-4 animate-spin" /> Matching brands…
          </div>
        )}

        {!loading && rows.length > 0 && groupedRows.map(([group, groupRows]) => (
          <div key={group} className="mt-5">
            {groupedRows.length > 1 && (
              <div className="mb-2 text-[11px] uppercase tracking-[0.2em] text-emerald-300 font-bold" data-testid={`franchise-group-${businessIdeaId}`}>
                {group}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {groupRows.map((f) => {
              const isSel = selectedIds.has(f.id);
              const unlock = unlocked[f.id];
              const isUnlocked = Boolean(unlock);
              const isSpotlight = spotlightBrandId && f.id === spotlightBrandId;
              return (
                <Card
                  key={f.id}
                  data-testid={`franchise-card-${f.id}`}
                  className={
                    "rounded-2xl transition-all bg-white " +
                    (isUnlocked ? "cursor-default " : "cursor-pointer ") +
                    (isSel
                      ? "border-navy ring-2 ring-navy shadow-md"
                      : isUnlocked
                        ? "border-emerald-300 shadow-sm"
                        : "border-slate-200 hover:shadow-lg hover:border-navy") +
                    (isSpotlight && !isUnlocked ? " ring-4 ring-navy shadow-lg animate-pulse-slow" : "")
                  }
                  onClick={() => {
                    if (directory) { if (f.slug) nav(`/franchise/${f.slug}`); return; }
                    if (!isUnlocked) handleToggle(f);
                  }}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      {/* R110.141 — Hide the select checkbox once the
                          brand is unlocked; users have already seen
                          the contact details, no reason to offer
                          another select. */}
                      {!isUnlocked && !directory && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSel}
                            onCheckedChange={() => handleToggle(f)}
                            data-testid={`franchise-select-${f.id}`}
                            className="mt-1 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                          />
                        </div>
                      )}
                      {/* R110.129 — Brand identity is now surfaced upfront.
                          Only contact PII (phone/email/brochure) stays
                          gated behind the unlock+CPL debit. */}
                      {(unlock?.brand_name || f.logo_url) ? (
                        f.logo_url && !unlock?.brand_name ? (
                          <img
                            src={f.logo_url}
                            alt={f.brand_name || "Brand logo"}
                            className="h-12 w-12 rounded-lg object-cover border border-slate-100 shrink-0"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
                            <Building2 className="h-5 w-5" />
                          </div>
                        )
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
                          <Building2 className="h-5 w-5" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-heading text-base font-bold text-slate-900 leading-snug line-clamp-2 break-words">
                          {unlock?.brand_name || f.brand_name || f.teaser_title || "Verified Franchise Brand"}
                        </div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-1">
                          <ShieldCheck className="h-3 w-3 text-emerald-600" />
                          {unlock?.brand_name
                            ? "Contact revealed"
                            : "Verified Brand · phone/email unlocks on submit"}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 space-y-1.5 text-[13px] text-slate-700">
                      <div className="flex items-center gap-1.5">
                        <Wallet className="h-3.5 w-3.5 text-emerald-600" />
                        <span className="text-slate-500">Investment:</span>{" "}
                        <b className="text-slate-900">
                          {inrCompact(f.minimum_investment)}
                          {f.maximum_investment && f.maximum_investment > f.minimum_investment
                            ? ` – ${inrCompact(f.maximum_investment)}`
                            : "+"}
                        </b>
                      </div>
                      {(f.payback_months || f.typical_roi_months) && (
                        <div className="text-slate-600">
                          <span className="text-slate-500">Payback:</span>{" "}
                          <b className="text-slate-900">
                            {f.payback_months || f.typical_roi_months} months
                          </b>
                        </div>
                      )}
                      {f.city_allocation && (
                        <div className="text-slate-600 truncate">
                          <span className="text-slate-500">Cities:</span>{" "}
                          <b className="text-slate-900">{f.city_allocation}</b>
                        </div>
                      )}
                    </div>

                    {/* R110.150 — Click-to-read brand details (public teaser
                        fields: about / model / year / space / royalty). PII
                        stays gated behind the unlock box below. */}
                    {(f.about_brand || f.franchise_model || f.year_established || f.space_required_sqft || f.royalty_percent) && (
                      <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => toggleExpand(f.id)}
                          data-testid={`franchise-details-toggle-${f.id}`}
                          aria-expanded={expanded.has(f.id)}
                          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 transition"
                        >
                          <Info className="h-3.5 w-3.5" />
                          {expanded.has(f.id) ? "Hide brand details" : "About this brand"}
                          <ChevronDown className={`h-3 w-3 transition-transform ${expanded.has(f.id) ? "rotate-180" : ""}`} />
                        </button>
                        {expanded.has(f.id) && (
                          <div
                            data-testid={`franchise-details-${f.id}`}
                            className="mt-2 rounded-xl border border-slate-200 bg-slate-50/70 p-3 text-[11px] text-slate-700 space-y-2"
                          >
                            {f.about_brand && (
                              <p className="leading-snug">{f.about_brand}</p>
                            )}
                            {f.franchise_model && (
                              <div>
                                <span className="text-slate-500">Model:</span>{" "}
                                <b className="text-slate-900">{f.franchise_model}</b>
                              </div>
                            )}
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-slate-600">
                              {f.year_established && <span>Est. <b className="text-slate-900">{f.year_established}</b></span>}
                              {f.space_required_sqft && <span>Space <b className="text-slate-900">{Number(f.space_required_sqft).toLocaleString("en-IN")} sq ft</b></span>}
                              {(f.royalty_percent || f.royalty_percent === 0) && f.royalty_percent != null && (
                                <span>Royalty <b className="text-slate-900">{f.royalty_percent}%</b></span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {unlock ? (
                      <div
                        data-testid={`franchise-unlocked-${f.id}`}
                        className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-xs text-slate-800 space-y-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">
                          Connected · {unlock.brand_name || "Brand"} team will call you soon
                        </div>
                        {/* R110.131 — Enriched post-unlock brand details:
                            logo, about-brand, franchise model, year and
                            contact info revealed only after submit. */}
                        {(unlock.logo_url || unlock.year_established) && (
                          <div className="flex items-center gap-2">
                            {unlock.logo_url ? (
                              <img
                                src={unlock.logo_url}
                                alt={unlock.brand_name || "Brand logo"}
                                className="h-9 w-9 rounded-md object-cover border border-emerald-200"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                            ) : null}
                            <div className="min-w-0">
                              <div className="font-heading text-sm font-bold text-slate-900 leading-snug line-clamp-2 break-words">
                                {unlock.brand_name}
                              </div>
                              {unlock.year_established ? (
                                <div className="text-[10px] text-slate-500">
                                  Est. {unlock.year_established}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        )}
                        {unlock.about_brand ? (
                          <p className="text-[11px] text-slate-700 leading-snug">
                            {unlock.about_brand}
                          </p>
                        ) : null}
                        {unlock.franchise_model ? (
                          <div className="text-[11px] text-slate-600">
                            <span className="text-slate-500">Model:</span>{" "}
                            <b className="text-slate-900">{unlock.franchise_model}</b>
                          </div>
                        ) : null}
                        {unlock.owner_phone && (
                          <div className="flex items-center gap-1.5">
                            <Phone className="h-3 w-3" />
                            <a href={`tel:${unlock.owner_phone}`} className="underline underline-offset-2 font-mono">
                              {unlock.owner_phone}
                            </a>
                          </div>
                        )}
                        {unlock.owner_email && (
                          <div className="flex items-center gap-1.5">
                            <Mail className="h-3 w-3" />
                            <a href={`mailto:${unlock.owner_email}`} className="underline underline-offset-2 break-all">
                              {unlock.owner_email}
                            </a>
                          </div>
                        )}
                        {unlock.brochure_url && (
                          <div className="flex items-center gap-1.5">
                            <FileDown className="h-3 w-3" />
                            <a href={unlock.brochure_url} target="_blank" rel="noreferrer" className="underline underline-offset-2">
                              Download brochure
                            </a>
                          </div>
                        )}
                      </div>
                    ) : directory ? (
                      <button
                        type="button"
                        data-testid={`franchise-dir-cta-${f.id}`}
                        onClick={(e) => { e.stopPropagation(); if (f.slug) nav(`/franchise/${f.slug}`); }}
                        className="mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-2.5 transition-colors"
                      >
                        <ShieldCheck className="h-4 w-4" /> View &amp; unlock contact details
                      </button>
                    ) : (
                      <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
                        <ShieldCheck className="h-3 w-3 text-emerald-600" />
                        {isSel
                          ? "Unlock brand contact details after you submit"
                          : "☐ Unlock brand contact details"}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default FranchiseMatchSection;
